import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Check, RotateCw } from "lucide-react";
import { toast } from "sonner";

const COST = 4;

export default function SparkDialog({ open, onOpenChange, projectId, chapterId, prevChapterId, onInsert }) {
  const [idea, setIdea] = useState("");
  const [cont, setCont] = useState(false);
  const [length, setLength] = useState("short");
  const [pov, setPov] = useState("");
  const [tone, setTone] = useState("");
  const [matchVoice, setMatchVoice] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null); // {draft_html, draft_text}

  const generate = async () => {
    if (!idea.trim() && !(cont && prevChapterId)) { toast.error("Add an idea, or turn on ‘continue from previous chapter’"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/spark`, {
        idea, continue_from_chapter_id: cont ? prevChapterId : null, length, pov, tone, match_voice: matchVoice,
      });
      setDraft(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error === "out_of_credits") toast.error(`You need ${d.needed} credits (you have ${d.balance}).`);
      else toast.error(formatApiErrorDetail(d) || "Couldn't draft — try again");
    } finally { setBusy(false); }
  };

  const insert = async () => {
    onInsert(draft.draft_html);
    toast.success("Draft added to your chapter");
    try {
      const { data } = await api.post(`/projects/${projectId}/codex/auto-detect`, { text: draft.draft_text, chapter_title: "" });
      const n = data.added?.length || 0;
      const d = data.died?.length || 0;
      if (n) toast.success(`${n} new character${n > 1 ? "s" : ""} added to your Codex`);
      if (d) toast.info(`${data.died.join(", ")} marked as KIA in your Codex`);
    } catch { /* non-blocking */ }
    reset();
    onOpenChange(false);
  };
  const reset = () => { setDraft(null); };

  const sel = "text-sm bg-background border border-border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary";

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg" data-testid="spark-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Spark a chapter</DialogTitle>
          <DialogDescription>Give an idea (or continue your last chapter) and I'll draft it in your voice, using your Codex. Uses {COST} credits per draft — it's a starting point you can edit.</DialogDescription>
        </DialogHeader>

        {!draft ? (
          <div className="space-y-3">
            <textarea value={idea} onChange={(e) => setIdea(e.target.value)} rows={3}
              placeholder="What happens in this chapter? e.g. ‘Coops reaches the ditch and finds the other soldier isn't who the voice claimed.’"
              data-testid="spark-idea"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
            {prevChapterId && (
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="spark-continue">
                <input type="checkbox" checked={cont} onChange={(e) => setCont(e.target.checked)} className="accent-primary" />
                Continue from my previous chapter
              </label>
            )}
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="spark-match-voice">
              <input type="checkbox" checked={matchVoice} onChange={(e) => setMatchVoice(e.target.checked)} className="accent-primary" />
              Match my writing voice <span className="text-xs text-muted-foreground">(uses your existing chapters as a style guide)</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <select value={length} onChange={(e) => setLength(e.target.value)} className={sel} data-testid="spark-length">
                <option value="scene">Short scene</option>
                <option value="short">Short chapter</option>
                <option value="full">Full chapter</option>
              </select>
              <input value={pov} onChange={(e) => setPov(e.target.value)} placeholder="POV (optional)" className={sel} data-testid="spark-pov" />
              <input value={tone} onChange={(e) => setTone(e.target.value)} placeholder="Tone (optional)" className={sel} data-testid="spark-tone" />
            </div>
            <Button onClick={generate} disabled={busy} className="w-full gap-2" data-testid="spark-generate">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Drafting…" : `Draft it (${COST} credits)`}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="max-h-72 overflow-y-auto rounded-lg border border-border bg-secondary/20 p-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid="spark-preview">
              {draft.draft_text}
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={insert} className="flex-1 gap-2" data-testid="spark-insert"><Check className="w-4 h-4" /> Insert into chapter</Button>
              <Button variant="outline" onClick={() => { setDraft(null); generate(); }} disabled={busy} className="gap-2" data-testid="spark-regenerate">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />} Regenerate ({COST})
              </Button>
            </div>
            <button onClick={reset} className="text-xs text-muted-foreground hover:text-foreground w-full text-center" data-testid="spark-back">← Change the idea</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
